import {apiFetchWithRefresh} from "@/lib/api";
import {isFirstPartyArtifactUrl, sameOriginArtifactUrl} from "@/utils/artifactUrl";

/**
 * Thrown by `downloadGeneratedFile` so callers can branch on transport vs
 * HTTP failure and render localized text. Keeping UI strings out of this
 * util means the helper can stay unit-testable without dragging the toast
 * system into its tests.
 */
export class DownloadError extends Error {
   readonly status?: number;
   constructor(message: string, status?: number) {
      super(message);
      this.name = "DownloadError";
      this.status = status;
   }
}

// Some browsers (older Firefox, older mobile WebKit) cancel an in-flight
// download if the blob URL is revoked too aggressively. 1s matches the
// FileSaver.js convention and is comfortably past the click dispatch on
// every supported browser.
const REVOKE_DELAY_MS = 1000;

/**
 * Force a browser download for a generated-file URL.
 *
 * The HTML5 `download` attribute is ignored when an anchor points at a
 * different origin — generated-file URLs name the Wingman API host while the
 * app runs on `app.*.meetgail.com`, so a plain `<a href download>` opens the
 * PDF in the browser instead of saving it. We work around it by fetching the
 * bytes ourselves and triggering the download from an in-memory blob URL,
 * which IS same-origin.
 *
 * First-party (Wingman) URLs are fetched through the same-origin signed-in
 * rewrite (`sameOriginArtifactUrl`), so the session cookie only ever goes to
 * our origin and Wingman receives a bearer token. Those fetches also go
 * through the shared on-401-refresh-and-retry recipe so a stale access token
 * recovers silently (like chat sends) instead of surfacing a false "session
 * expired". Only first-party 401s say something about our session — a
 * third-party 401 (e.g. an expired GCS signed URL) must not trigger an Auth0
 * refresh.
 *
 * Throws `DownloadError` so callers can map the failure to user-facing UI.
 */
export const downloadGeneratedFile = async (url: string, fileName: string): Promise<void> => {
   const firstParty = isFirstPartyArtifactUrl(url);
   const requestUrl = sameOriginArtifactUrl(url);
   let res: Response;
   try {
      res = await apiFetchWithRefresh(() => fetch(requestUrl, {credentials: "same-origin"}), {
         retryOn401: firstParty,
      });
   } catch (err) {
      throw new DownloadError(err instanceof Error ? err.message : "network_error");
   }
   if (!res.ok) throw new DownloadError(`HTTP ${res.status}`, res.status);
   const sourceBlob = await res.blob();
   // Re-wrap as octet-stream so the browser can't render the bytes inline.
   // When `a.click()` happens after an `await`, Chrome sometimes treats the
   // anchor as no longer being inside a user gesture and ignores the
   // `download` attribute, falling back to navigation. For an
   // `application/pdf` blob that means the PDF opens in a new tab; for
   // `application/octet-stream` the browser has no inline renderer and is
   // forced to download.
   const downloadBlob = new Blob([sourceBlob], {type: "application/octet-stream"});
   const objectUrl = URL.createObjectURL(downloadBlob);
   try {
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
   } finally {
      setTimeout(() => URL.revokeObjectURL(objectUrl), REVOKE_DELAY_MS);
   }
};
